import { useEffect, useRef } from 'react'
import { useChatLayout } from '../context/ChatLayoutContext.jsx'
import {
  getPageViewCountFromStorage,
  updatePageViewCount,
} from '../lib/widgetPageViewTracking.js'

/** Matches ochatbot-builder globalConstants trigger_type values. */
const LEGACY_TRIGGER_TYPE = {
  '1': 'do_nothing',
  '2': 'open_widget',
  '3': 'open_widget',
  '4': 'trigger_once',
  '5': 'never',
  '6': 'do_nothing',
  '7': 'inline_chat',
}

/** Matches ochatbot-builder globalConstants trigger_show (timing) values. */
const LEGACY_TRIGGER_TIMING = {
  '1': 'all',
  '2': 'after_seconds',
  '3': 'middle_page',
  '4': 'bottom_page',
  '5': 'exit',
  '7': 'page_views',
}

function pickDeviceBranch(isMobile) {
  return isMobile ? 'mobile' : 'desktop'
}

function normalizeTriggerType(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  return LEGACY_TRIGGER_TYPE[s] ?? s
}

function normalizeTriggerTiming(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return 'all'
  return LEGACY_TRIGGER_TIMING[s] ?? s
}

function readAiDeviceTrigger(conf, isMobile) {
  const tc = conf?.triggerConfig
  if (!tc || typeof tc !== 'object') return null
  const branch = pickDeviceBranch(isMobile)
  const block = tc[branch]
  if (!block || typeof block !== 'object') return null
  const triggerType = normalizeTriggerType(block.triggerType)
  if (!triggerType) return null
  return {
    ...block,
    triggerType,
    triggerTiming: normalizeTriggerTiming(block.triggerTiming),
    triggerValue: Number(block.triggerValue) || 0,
  }
}

function readUrlFilterDeviceTrigger(conf, isMobile) {
  const uft = conf?.urlFilterTriggers
  if (!uft || typeof uft !== 'object') return null

  const rawType = isMobile ? uft.mobileTriggerType : uft.triggerType
  const rawShow = isMobile ? uft.mobileTriggerShow : uft.triggerShow
  const rawVal = isMobile ? uft.mobileTriggerValue : uft.triggerValue
  const rawText = isMobile ? uft.mobileTriggerText : uft.triggerText

  const triggerType = normalizeTriggerType(rawType)
  if (!triggerType) return null

  return {
    triggerType,
    triggerTiming: normalizeTriggerTiming(rawShow),
    triggerValue: Number(rawVal) || 0,
    triggerText: String(rawText || ''),
    visualEffect: 'none',
  }
}

function readDeviceTrigger(conf, isMobile) {
  return readAiDeviceTrigger(conf, isMobile) ?? readUrlFilterDeviceTrigger(conf, isMobile)
}

function triggerStorageAgentId(conf) {
  return String(conf?.chatId || conf?.agentId || '')
}

function hasTriggerOnceFired(conf) {
  const aid = triggerStorageAgentId(conf)
  const lq = String(conf?.launchConversationId ?? '')
  if (!aid || !lq) return false
  try {
    const raw = localStorage.getItem(`ochatbot_triggers_${aid}`)
    if (!raw) return false
    const history = JSON.parse(raw)
    const row = history[lq]
    if (!row || row.triggerCount < 1) return false
    return row.pageUrl === window.location.href
  } catch {
    return false
  }
}

function recordTriggerOnce(conf) {
  const aid = triggerStorageAgentId(conf)
  const lq = String(conf?.launchConversationId ?? '')
  if (!aid || !lq) return
  try {
    const raw = localStorage.getItem(`ochatbot_triggers_${aid}`)
    const history = raw ? JSON.parse(raw) : {}
    const now = new Date().toISOString()
    history[lq] = {
      firstTriggered: history[lq]?.firstTriggered || now,
      lastTriggered: now,
      triggerCount: (history[lq]?.triggerCount || 0) + 1,
      triggerType: 'trigger_once',
      pageUrl: window.location.href,
    }
    localStorage.setItem(`ochatbot_triggers_${aid}`, JSON.stringify(history))
  } catch {
    // ignore quota / JSON errors
  }
}

/** Same scroll depth as v1 useTriggerTiming (safe denominator). */
function scrollDepthPercent() {
  const scrollTop = window.scrollY ?? window.pageYOffset ?? 0
  const winH = window.innerHeight || 1
  const docH = Math.max(
    document.documentElement?.scrollHeight ?? 0,
    document.body?.scrollHeight ?? 0,
    winH,
  )
  const denom = Math.max(docH - winH, 1)
  return (scrollTop / denom) * 100
}

function isTimingAlreadyMet(timing, triggerValue, agentId, isDesktop) {
  switch (timing) {
    case 'all':
      return true
    case 'after_seconds':
      return false
    case 'middle_page':
      return scrollDepthPercent() >= 50
    case 'bottom_page':
      return scrollDepthPercent() >= 90
    case 'exit':
      return false
    case 'page_views': {
      const n = getPageViewCountFromStorage(agentId)
      return n >= triggerValue
    }
    default:
      return false
  }
}

function canAutoOpenWidget(triggerType) {
  return triggerType === 'open_widget' || triggerType === 'trigger_once'
}

/**
 * Mirrors ometrics-ai v1 useTriggerSystem + useTriggerTiming: URL-filter and AI
 * triggerConfig, legacy numeric codes, and timing modes all / after_seconds /
 * middle_page / bottom_page / exit (desktop) / page_views.
 *
 * v1 `inline_chat` shows an inline bubble without opening the panel; v2 has no
 * inline bubble yet, so that type does not schedule listeners or open here.
 */
export function useWidgetAiTriggers(conf) {
  const { open, openChat } = useChatLayout()
  const ranRef = useRef(false)

  useEffect(() => {
    if (!conf || conf.previewMode) return
    const aid = triggerStorageAgentId(conf)
    if (!aid) return
    updatePageViewCount(aid)
  }, [conf?.previewMode, conf?.agentId, conf?.chatId])

  useEffect(() => {
    if (!conf || conf.previewMode) return
    if (open || ranRef.current) return

    const launchRaw = conf.launchConversationId
    if (launchRaw == null || launchRaw === '' || !conf.chatServer) return

    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    const isDesktop = !isMobile

    const device = readDeviceTrigger(conf, isMobile)
    if (!device) return

    const triggerType = String(device.triggerType || '')
    const timing = String(device.triggerTiming || 'all')
    const triggerValue = Number(device.triggerValue) || 0

    if (triggerType === 'never' || triggerType === 'do_nothing') return
    if (triggerType === 'inline_chat') return

    const agentId = String(conf.agentId || conf.chatId || '')
    const launchQuestion = String(launchRaw)
    const globalLockKey = `ometrics_trigger_${agentId}_${launchQuestion}`

    try {
      if (localStorage.getItem(globalLockKey) === 'processed') return
    } catch {
      // continue
    }

    const fire = () => {
      if (ranRef.current) return
      ranRef.current = true
      try {
        localStorage.setItem(globalLockKey, 'processed')
      } catch {
        // ignore
      }
      openChat()
      if (triggerType === 'trigger_once') {
        recordTriggerOnce(conf)
      }
    }

    const tryOpen = () => {
      if (!canAutoOpenWidget(triggerType)) return
      if (triggerType === 'trigger_once' && hasTriggerOnceFired(conf)) return
      fire()
    }

    if (!canAutoOpenWidget(triggerType)) return undefined

    if (timing === 'all') {
      tryOpen()
      return undefined
    }

    const pvAgentId = triggerStorageAgentId(conf)

    if (timing === 'after_seconds') {
      if (triggerValue > 0) {
        const t = window.setTimeout(tryOpen, triggerValue * 1000)
        return () => window.clearTimeout(t)
      }
      tryOpen()
      return undefined
    }

    if (
      isTimingAlreadyMet(timing, triggerValue, pvAgentId, isDesktop) &&
      timing !== 'after_seconds' &&
      timing !== 'exit'
    ) {
      tryOpen()
      return undefined
    }

    const cleanups = []

    if (timing === 'middle_page') {
      const onScroll = () => {
        if (ranRef.current) return
        if (scrollDepthPercent() >= 50) {
          window.removeEventListener('scroll', onScroll)
          tryOpen()
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true })
      cleanups.push(() => window.removeEventListener('scroll', onScroll))
    }

    if (timing === 'bottom_page') {
      const onScroll = () => {
        if (ranRef.current) return
        if (scrollDepthPercent() >= 90) {
          window.removeEventListener('scroll', onScroll)
          tryOpen()
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true })
      cleanups.push(() => window.removeEventListener('scroll', onScroll))
    }

    if (timing === 'exit' && isDesktop) {
      const onMouseOut = (e) => {
        if (ranRef.current) return
        if (e.clientY <= 0) {
          document.removeEventListener('mouseout', onMouseOut)
          tryOpen()
        }
      }
      document.addEventListener('mouseout', onMouseOut)
      cleanups.push(() => document.removeEventListener('mouseout', onMouseOut))
    }

    if (timing === 'page_views' && pvAgentId) {
      const threshold = Math.max(1, triggerValue)
      let intervalId = null
      const storageKey = `ometrics_page_view_${pvAgentId}`
      function disposePageViews() {
        if (intervalId != null) {
          window.clearInterval(intervalId)
          intervalId = null
        }
        window.removeEventListener('storage', onStorage)
      }
      function tick() {
        if (ranRef.current) return
        if (getPageViewCountFromStorage(pvAgentId) >= threshold) {
          tryOpen()
          disposePageViews()
        }
      }
      function onStorage(e) {
        if (e.key === storageKey && e.newValue) tick()
      }
      window.addEventListener('storage', onStorage)
      intervalId = window.setInterval(tick, 1000)
      cleanups.push(disposePageViews)
    }

    if (cleanups.length === 0) {
      return undefined
    }

    return () => {
      cleanups.forEach((fn) => {
        try {
          fn()
        } catch {
          // ignore
        }
      })
    }
  }, [conf, open, openChat])
}
